import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'

const settingsList = [
  { key: 'showResults', label: 'Assessment Results', description: 'Completed assessment scores and reports' },
  { key: 'showMaesterReport', label: 'M365 Security Report', description: 'Maester scan results and configuration audit' },
  { key: 'showVault', label: 'Document Vault', description: 'Secure document repository for shared files' }
]

const retainerSettings = [
  { key: 'showPlan', label: 'Enablement Plan', description: 'Phased security improvement roadmap' },
  { key: 'showPolicies', label: 'Policies', description: 'Published security policies for client review and acknowledgment' },
  { key: 'showSecurity', label: 'Security Profile', description: 'Risk profile, critical systems, and compliance frameworks' }
]

function ToggleRow({ setting, checked, saving, onToggle }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{setting.label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{setting.description}</p>
      </div>
      <button
        onClick={() => onToggle(setting.key)}
        disabled={saving}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
          checked ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-600'
        } ${saving ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        role="switch"
        aria-checked={checked}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

function PortalSettingsPanel({ client }) {
  const { updateClient } = useAssessment()
  const [saving, setSaving] = useState(false)

  const isRetainer = client.status === 'retainer'

  const portalSettings = client.portalSettings || {
    showResults: true,
    showMaesterReport: true,
    showVault: false,
    showPlan: true,
    showPolicies: true,
    showSecurity: true
  }

  const handleToggle = async (key) => {
    const updated = { ...portalSettings, [key]: !portalSettings[key] }
    setSaving(true)
    await updateClient(client.id, { portalSettings: updated })
    setSaving(false)
  }

  const portalEnabled = portalSettings.portalEnabled !== false

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Client Portal Settings</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Control what the client sees on their portal dashboard
          </p>
        </div>
        <Link
          to={`/portal/${client.id}`}
          className="text-xs text-primary-600 hover:text-primary-700 dark:text-accent-300 dark:hover:text-accent-200 flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview Client View
        </Link>
      </div>

      <div className={`space-y-3 ${!portalEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
        {settingsList.map(setting => (
          <ToggleRow key={setting.key} setting={setting} checked={portalSettings[setting.key]} saving={saving} onToggle={handleToggle} />
        ))}
      </div>

      {isRetainer && (
        <div className={!portalEnabled ? 'opacity-40 pointer-events-none' : ''}>
          <div className="mt-5 mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Retainer</p>
          </div>
          <div className="space-y-3">
            {retainerSettings.map(setting => (
              <ToggleRow key={setting.key} setting={setting} checked={portalSettings[setting.key]} saving={saving} onToggle={handleToggle} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PortalSettingsPanel
